import { useState, type CSSProperties, type ReactNode } from 'react';
import {
  ArrowLeft,
  Bell,
  BookOpen,
  CalendarDays,
  Check,
  ChevronRight,
  Home,
  FileText,
  MessageSquare,
  Mic,
  Search,
  Settings,
  Sparkles,
  Target,
  TrendingUp,
  UserRound,
} from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile';
import mockAvatarImage from '../assets/images/M1.png';
import mockReporterImage from '../assets/images/M2.png';

const COLORS = {
  bg: '#FAFAF7',
  surface: '#FFFFFF',
  ink: '#18181B',
  muted: '#71717A',
  line: '#E4E4E7',
  cyan: '#0E7490',
  cyanSoft: '#ECFEFF',
  purple: '#7C3AED',
  purpleSoft: '#F5F3FF',
  rose: '#BE123C',
  roseSoft: '#FFF1F2',
  green: '#15803D',
  greenSoft: '#DCFCE7',
  amber: '#B45309',
  amberSoft: '#FEF3C7',
};

const FONT_FAMILY = '"Hiragino Sans", "Noto Sans JP", "Yu Gothic", "Meiryo", system-ui, sans-serif';

type View =
  | 'home'
  | 'courses'
  | 'records'
  | 'settings'
  | 'profile'
  | 'interview'
  | 'patient-score'
  | 'report-interview'
  | 'report-score';

interface Course {
  id: number;
  category: string;
  behaviorChange: boolean;
  title: string;
  description: string;
  interview: string;
  minutes: number;
  completed: boolean;
  score?: number;
}

const CATEGORY_OPTIONS = [
  '1．問診',
  '2．ブラッシング指導',
  '3．歯磨剤',
  '4．補助器具（フロス・歯間ブラシ・タフトブラシ）',
  '5．マウスウォッシュ',
  '6．電動歯ブラシ',
  '7．ジェットウォッシャー',
  '8．飲み物と食べ物・嗜好',
  '9．喫煙・受動喫煙の状態',
  '10．糖尿病',
  '11．高血圧症・循環器疾患',
  '12．複合アバター',
];

function categoryName(category: string): string {
  return category.replace(/^\d+．/, '');
}

function coursesForCategory(category: string): Course[] {
  const name = categoryName(category);
  const categoryIndex = Math.max(0, CATEGORY_OPTIONS.indexOf(category));
  return [
    {
      id: categoryIndex * 2 + 1,
      category: name,
      behaviorChange: false,
      title: `${name}に関する情報聴取`,
      description: '患者さんの状態や習慣を、自然な会話から整理して聞き取ります。',
      interview: '患者面接',
      minutes: 10,
      completed: categoryIndex < 2,
      score: categoryIndex < 2 ? 84 : undefined,
    },
    {
      id: categoryIndex * 2 + 2,
      category: name,
      behaviorChange: true,
      title: `${name}の情報聴取から改善指導`,
      description: '聞き取った内容をもとに、患者さんと実行可能な改善案を検討します。',
      interview: '患者面接・歯科医師報告',
      minutes: 15,
      completed: false,
    },
  ];
}

const CATEGORY_PROGRESS = [
  { label: '問診', done: 5, total: 5 },
  { label: 'ブラッシング指導', done: 4, total: 6 },
  { label: 'フロス', done: 3, total: 6 },
  { label: '歯間ブラシ', done: 4, total: 6 },
  { label: 'マウスウォッシュ', done: 2, total: 6 },
  { label: '電動歯ブラシ', done: 3, total: 6 },
];

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.15em', color: COLORS.muted }}>
      {children}
    </div>
  );
}

function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <section
      style={{
        background: COLORS.surface,
        border: `1px solid ${COLORS.line}`,
        borderRadius: 12,
        padding: 20,
        ...style,
      }}
    >
      {children}
    </section>
  );
}

function BehaviorChangeChip({ behaviorChange }: { behaviorChange: boolean }) {
  const color = behaviorChange ? COLORS.purple : COLORS.cyan;
  const soft = behaviorChange ? COLORS.purpleSoft : COLORS.cyanSoft;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        borderRadius: 999,
        padding: '5px 10px',
        color,
        background: soft,
        fontSize: 12,
        fontWeight: 800,
      }}
    >
      {behaviorChange ? '行動変容あり' : '行動変容なし'}
    </span>
  );
}

function ProgressBar({ value, color = COLORS.cyan }: { value: number; color?: string }) {
  return (
    <div style={{ height: 6, borderRadius: 999, background: COLORS.line, overflow: 'hidden' }}>
      <div style={{ width: `${Math.max(0, Math.min(100, value))}%`, height: '100%', borderRadius: 999, background: color }} />
    </div>
  );
}

function OutlineButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: `1px solid ${COLORS.line}`,
        borderRadius: 8,
        padding: '8px 12px',
        background: COLORS.surface,
        color: COLORS.ink,
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
      }}
    >
      {children}
    </button>
  );
}

export default function StudentHomeMock() {
  const isMobile = useIsMobile();
  const [view, setView] = useState<View>('home');
  const [category, setCategory] = useState(CATEGORY_OPTIONS[0]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [goalMinutes, setGoalMinutes] = useState(30);

  const filteredCourses = coursesForCategory(category);

  const openCourse = (course: Course) => {
    setSelectedCourse(course);
    setView('interview');
  };

  const goToCourses = () => {
    setSelectedCourse(null);
    setView('courses');
  };

  type NavigationView = Exclude<View, 'interview' | 'patient-score' | 'report-interview' | 'report-score'>;
  const navItems: Array<{ id: NavigationView; label: string; icon: ReactNode }> = [
    { id: 'home', label: 'ホーム', icon: <Home size={17} /> },
    { id: 'courses', label: '学習', icon: <BookOpen size={17} /> },
    { id: 'records', label: '学習スコア', icon: <TrendingUp size={17} /> },
    { id: 'settings', label: '設定', icon: <Settings size={17} /> },
    { id: 'profile', label: 'マイページ', icon: <UserRound size={17} /> },
  ];

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bg, color: COLORS.ink, fontFamily: FONT_FAMILY }}>
      <header style={{ background: COLORS.cyan, borderBottom: `1px solid ${COLORS.cyan}` }}>
        <div
          style={{
            maxWidth: 760,
            margin: '0 auto',
            minHeight: 68,
            padding: isMobile ? '0 16px' : '0 32px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <button
            type="button"
            onClick={() => setView('home')}
            aria-label="ホームへ戻る"
            style={{ border: 0, background: 'transparent', padding: 0, color: COLORS.surface, cursor: 'pointer', textAlign: 'left' }}
          >
            <div style={{ fontSize: 21, fontWeight: 900, letterSpacing: -0.4 }}>Avia Dental</div>
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" aria-label="検索" style={headerIconButtonStyle}>
            <Search size={22} />
          </button>
          <button type="button" aria-label="お知らせ" style={headerIconButtonStyle}>
            <Bell size={18} />
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 760, margin: '0 auto', padding: isMobile ? '14px 12px 104px' : '20px 24px 112px' }}>
        {view === 'home' && <HomeView isMobile={isMobile} setView={setView} />}
        {view === 'courses' && (
          <CoursesView
            category={category}
            setCategory={setCategory}
            courses={filteredCourses}
            openCourse={openCourse}
          />
        )}
        {view === 'records' && <RecordsView />}
        {view === 'settings' && <SettingsView goalMinutes={goalMinutes} setGoalMinutes={setGoalMinutes} />}
        {view === 'profile' && <ProfileView />}
        {view === 'interview' && selectedCourse && (
          <InterviewMock
            course={selectedCourse}
            onBack={goToCourses}
            onScore={() => setView('patient-score')}
            onReport={() => setView('report-interview')}
          />
        )}
        {view === 'patient-score' && selectedCourse && (
          <ScoreResultMock maxScore={50} score={42} course={selectedCourse} onBack={goToCourses} />
        )}
        {view === 'report-interview' && selectedCourse && (
          <ReportInterviewMock
            course={selectedCourse}
            onBack={() => setView('interview')}
            onComplete={() => setView('report-score')}
          />
        )}
        {view === 'report-score' && selectedCourse && (
          <ScoreResultMock maxScore={100} score={84} course={selectedCourse} onBack={goToCourses} />
        )}
      </main>

      <nav
        aria-label="学生向けメニュー"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 20,
          background: COLORS.surface,
          borderTop: `1px solid ${COLORS.line}`,
        }}
      >
        <div style={{ maxWidth: 760, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)' }}>
          {navItems.map((item) => {
            const active = view === item.id || (
              ['interview', 'patient-score', 'report-interview', 'report-score'].includes(view) && item.id === 'courses'
            );
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setView(item.id)}
                style={{
                  border: 0,
                  borderTop: active ? `3px solid ${COLORS.cyan}` : '3px solid transparent',
                  background: 'transparent',
                  color: active ? COLORS.cyan : COLORS.ink,
                  padding: '9px 2px 11px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 10,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                {item.icon}
                {item.label}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

const headerIconButtonStyle: CSSProperties = {
  width: 38,
  height: 38,
  display: 'grid',
  placeItems: 'center',
  borderRadius: 999,
  border: 0,
  background: 'transparent',
  color: COLORS.surface,
  cursor: 'pointer',
};

function PageHeading({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h1 style={{ margin: '6px 0 0', fontSize: 26, fontWeight: 900, letterSpacing: -0.5, lineHeight: 1.2 }}>{title}</h1>
      <p style={{ margin: '8px 0 0', fontSize: 13.5, lineHeight: 1.75, color: COLORS.muted }}>{description}</p>
    </div>
  );
}

function HomeView({ isMobile, setView }: { isMobile: boolean; setView: (view: View) => void }) {
  return (
    <>
      <Card style={{ marginBottom: 14, padding: isMobile ? 18 : 22 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>お知らせ</h1>
        <div style={{ marginTop: 12 }}>
          {[
            ['システムメンテナンスのお知らせ', '2026年8月17日'],
            ['「糖尿病」新カテゴリ追加', '2026年8月1日'],
          ].map(([title, date], index) => (
            <button
              key={title}
              type="button"
              style={{
                width: '100%',
                border: 0,
                borderTop: index === 0 ? `1px solid ${COLORS.line}` : 0,
                borderBottom: `1px solid ${COLORS.line}`,
                background: 'transparent',
                color: COLORS.ink,
                padding: '13px 2px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <span style={{ flex: 1, fontSize: 13.5, fontWeight: 800 }}>{title}</span>
              <span style={{ color: COLORS.muted, fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{date}</span>
              <ChevronRight size={16} color={COLORS.muted} />
            </button>
          ))}
        </div>
      </Card>

      <Card style={{ marginBottom: 14, padding: isMobile ? 18 : 22 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>学習進捗</h2>
        <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '220px 1fr', gap: isMobile ? 18 : 32, alignItems: 'center' }}>
          <div style={{ position: 'relative', width: 170, height: 170, margin: '0 auto' }}>
            <svg viewBox="0 0 120 120" width="170" height="170" role="img" aria-label="学習進捗69パーセント">
              <circle cx="60" cy="60" r="49" fill="none" stroke={COLORS.line} strokeWidth="10" />
              <circle
                cx="60"
                cy="60"
                r="49"
                fill="none"
                stroke={COLORS.cyan}
                strokeWidth="10"
                strokeLinecap="round"
                pathLength="100"
                strokeDasharray="69 100"
                transform="rotate(-90 60 60)"
              />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: 38, lineHeight: 1, fontWeight: 900, color: COLORS.cyan, fontVariantNumeric: 'tabular-nums' }}>69%</div>
                <div style={{ marginTop: 6, fontSize: 15, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>18/26</div>
                <div style={{ marginTop: 2, fontSize: 11.5, fontWeight: 700 }}>カリキュラム</div>
              </div>
            </div>
          </div>
          <div>
            <div style={{ display: 'grid', gap: 9, fontSize: 14, fontWeight: 800 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}><span>全カテゴリ</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>13</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}><span>講座タイプ</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>2種類</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}><span>全カリキュラム</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>26</span></div>
            </div>
            <button
              type="button"
              onClick={() => setView('records')}
              style={{ marginTop: 18, width: '100%', border: 0, borderRadius: 8, padding: '11px 14px', background: COLORS.cyanSoft, color: COLORS.cyan, fontSize: 13, fontWeight: 900, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}
            >
              <TrendingUp size={17} /> 学習推移
            </button>
          </div>
        </div>
      </Card>

      <Card style={{ padding: isMobile ? 18 : 22 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>カテゴリ別 学習状況</h2>
        <div style={{ marginTop: 12 }}>
          {CATEGORY_PROGRESS.map((item, index) => {
            const percent = Math.round((item.done / item.total) * 100);
            return (
              <div key={item.label} style={{ display: 'grid', gridTemplateColumns: '42px minmax(104px, 1fr) minmax(125px, 1.2fr)', alignItems: 'center', gap: 12, padding: '13px 0', borderBottom: index === CATEGORY_PROGRESS.length - 1 ? 0 : `1px solid ${COLORS.line}` }}>
                <span style={{ width: 38, height: 38, borderRadius: 8, display: 'grid', placeItems: 'center', color: COLORS.cyan, background: COLORS.cyanSoft }}>
                  {index % 2 === 0 ? <BookOpen size={19} /> : <Sparkles size={19} />}
                </span>
                <span style={{ fontSize: isMobile ? 13.5 : 15, fontWeight: 900, lineHeight: 1.35 }}>{item.label}</span>
                <div>
                  <div style={{ marginBottom: 6, textAlign: 'right', fontSize: 11.5, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{percent}%（{item.done}/{item.total}）</div>
                  <ProgressBar value={percent} />
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </>
  );
}

function CoursesView({ category, setCategory, courses, openCourse }: {
  category: string;
  setCategory: (category: string) => void;
  courses: Course[];
  openCourse: (course: Course) => void;
}) {
  return (
    <>
      <PageHeading eyebrow="SCENARIO SELECT" title="シナリオを選ぶ" description="学習カテゴリを選び、面接するシナリオを選択します。" />
      <Card style={{ marginBottom: 14 }}>
        <label style={{ display: 'grid', gap: 7, fontSize: 12, fontWeight: 800 }}>
          学習カテゴリ
          <select value={category} onChange={(event) => setCategory(event.target.value)} style={selectStyle}>
            {CATEGORY_OPTIONS.map((item) => <option key={item}>{item}</option>)}
          </select>
        </label>
        <p style={{ margin: '10px 0 0', color: COLORS.muted, fontSize: 11.5, lineHeight: 1.65 }}>
          複合アバターも含め、繰り返し練習することで全カテゴリを学習できます。
        </p>
      </Card>
      <div style={{ marginBottom: 9, color: COLORS.muted, fontSize: 12, fontWeight: 700 }}>{courses.length}件のシナリオ</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14 }}>
        {courses.map((course) => (
          <button key={course.id} type="button" onClick={() => openCourse(course)} style={{ border: `1px solid ${COLORS.line}`, borderRadius: 12, padding: 20, background: COLORS.surface, color: COLORS.ink, textAlign: 'left', cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}><BehaviorChangeChip behaviorChange={course.behaviorChange} />{course.completed && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: COLORS.green, fontSize: 11.5, fontWeight: 800 }}><Check size={14} />修了</span>}</div>
            <div style={{ marginTop: 14, color: COLORS.muted, fontSize: 10, fontWeight: 800, letterSpacing: '0.12em' }}>{course.category}</div>
            <h2 style={{ margin: '6px 0 0', fontSize: 15, lineHeight: 1.55, fontWeight: 900 }}>{course.title}</h2>
            <p style={{ margin: '8px 0 0', color: COLORS.muted, fontSize: 12.5, lineHeight: 1.65 }}>{course.description}</p>
            <div style={{ marginTop: 15, paddingTop: 13, borderTop: `1px solid ${COLORS.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, color: COLORS.muted, fontSize: 11.5, fontWeight: 700 }}>
              <span>{course.interview} · 約{course.minutes}分</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: COLORS.cyan, fontWeight: 900 }}>面接を開く <ChevronRight size={15} /></span>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}

const selectStyle: CSSProperties = {
  width: '100%',
  minHeight: 42,
  border: `1px solid ${COLORS.line}`,
  borderRadius: 8,
  padding: '0 12px',
  background: COLORS.surface,
  color: COLORS.ink,
  fontFamily: FONT_FAMILY,
  fontSize: 13,
  fontWeight: 700,
};

function InterviewMock({
  course,
  onBack,
  onScore,
  onReport,
}: {
  course: Course;
  onBack: () => void;
  onScore: () => void;
  onReport: () => void;
}) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <OutlineButton onClick={onBack}><ArrowLeft size={15} /> シナリオ選択へ</OutlineButton>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
          <div style={{ fontSize: 12, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{course.category}</div>
          <div style={{ marginTop: 4 }}><BehaviorChangeChip behaviorChange={course.behaviorChange} /></div>
        </div>
      </div>

      <Card style={{ marginTop: 14, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '13px 16px', borderBottom: `1px solid ${COLORS.line}` }}>
          <Eyebrow>AI AVATAR INTERVIEW</Eyebrow>
          <h1 style={{ margin: '6px 0 0', fontSize: 16, fontWeight: 900, lineHeight: 1.5 }}>{course.title}</h1>
        </div>
        <div style={{ background: COLORS.ink, display: 'grid', placeItems: 'center', minHeight: 330, maxHeight: 430, overflow: 'hidden' }}>
          <img src={mockAvatarImage} alt="面接患者のAIアバター" style={{ display: 'block', width: '100%', height: '100%', maxHeight: 430, objectFit: 'contain' }} />
        </div>
      </Card>

      <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12 }}>
        <button type="button" style={interviewToolButtonStyle}><FileText size={20} />課題文</button>
        <button type="button" aria-label="マイクを開始" style={microphoneButtonStyle}><Mic size={25} /><span>タップして話す</span></button>
        <button type="button" style={interviewToolButtonStyle}><BookOpen size={20} />問診票</button>
      </div>

      <Card style={{ marginTop: 14, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 900 }}><MessageSquare size={17} />会話ログ</div>
        <div style={{ marginTop: 13, display: 'grid', gap: 10, fontSize: 13, lineHeight: 1.7 }}>
          <div><strong style={{ color: COLORS.cyan }}>あなた：</strong>こんにちは。歯科衛生士の山田です。</div>
          <div><strong>患者：</strong>こんにちは。よろしくお願いします。</div>
        </div>
      </Card>

      <Card style={{ marginTop: 14, padding: 16 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 900 }}>患者面接を終了する</h2>
        <p style={{ margin: '6px 0 0', color: COLORS.muted, fontSize: 12, lineHeight: 1.65 }}>
          報告面接へ進むか、患者面接だけを採点するか選択します。
        </p>
        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
          <button type="button" onClick={onReport} style={solidActionButtonStyle}>
            歯科衛生士への報告へ進む <ChevronRight size={17} />
          </button>
          <button type="button" onClick={onScore} style={outlineActionButtonStyle}>
            報告面接をせずに採点（50点満点）
          </button>
        </div>
      </Card>
    </>
  );
}

const interviewToolButtonStyle: CSSProperties = {
  minHeight: 74,
  border: `1px solid ${COLORS.line}`,
  borderRadius: 12,
  background: COLORS.surface,
  color: COLORS.ink,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  fontSize: 12,
  fontWeight: 900,
  cursor: 'pointer',
};

const microphoneButtonStyle: CSSProperties = {
  width: 94,
  height: 94,
  border: `8px solid ${COLORS.greenSoft}`,
  borderRadius: 999,
  background: COLORS.green,
  color: COLORS.surface,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  fontSize: 10,
  fontWeight: 900,
  cursor: 'pointer',
};

const solidActionButtonStyle: CSSProperties = {
  minHeight: 46,
  border: 0,
  borderRadius: 8,
  padding: '11px 16px',
  background: COLORS.ink,
  color: COLORS.surface,
  fontSize: 13,
  fontWeight: 900,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
};

const outlineActionButtonStyle: CSSProperties = {
  minHeight: 46,
  border: `1px solid ${COLORS.line}`,
  borderRadius: 8,
  padding: '11px 16px',
  background: COLORS.surface,
  color: COLORS.ink,
  fontSize: 13,
  fontWeight: 900,
  cursor: 'pointer',
};

function ReportInterviewMock({
  course,
  onBack,
  onComplete,
}: {
  course: Course;
  onBack: () => void;
  onComplete: () => void;
}) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <OutlineButton onClick={onBack}><ArrowLeft size={15} /> 患者面接へ</OutlineButton>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
          <Eyebrow>REPORT INTERVIEW</Eyebrow>
          <div style={{ marginTop: 4, fontSize: 13, fontWeight: 900 }}>歯科衛生士への報告</div>
        </div>
      </div>

      <Card style={{ marginTop: 14, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '13px 16px', borderBottom: `1px solid ${COLORS.line}` }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.muted }}>{course.category}</div>
          <h1 style={{ margin: '5px 0 0', fontSize: 16, fontWeight: 900, lineHeight: 1.5 }}>患者面接の内容を簡潔に報告してください</h1>
        </div>
        <div style={{ background: COLORS.ink, display: 'grid', placeItems: 'center', minHeight: 330, maxHeight: 430, overflow: 'hidden' }}>
          <img src={mockReporterImage} alt="報告を受ける歯科衛生士のAIアバター" style={{ display: 'block', width: '100%', height: '100%', maxHeight: 430, objectFit: 'contain' }} />
        </div>
      </Card>

      <Card style={{ marginTop: 14, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 900 }}><MessageSquare size={17} />報告ログ</div>
        <div style={{ marginTop: 13, display: 'grid', gap: 10, fontSize: 13, lineHeight: 1.7 }}>
          <div><strong style={{ color: COLORS.cyan }}>あなた：</strong>患者さんの主訴と生活習慣について報告します。</div>
          <div><strong>歯科衛生士：</strong>はい。要点を順番に説明してください。</div>
        </div>
      </Card>

      <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12 }}>
        <button type="button" style={interviewToolButtonStyle}><FileText size={20} />患者面接メモ</button>
        <button type="button" aria-label="報告用マイクを開始" style={microphoneButtonStyle}><Mic size={25} /><span>タップして報告</span></button>
        <button type="button" style={interviewToolButtonStyle}><BookOpen size={20} />問診票</button>
      </div>

      <button type="button" onClick={onComplete} style={{ ...solidActionButtonStyle, width: '100%', marginTop: 14 }}>
        報告面接を終了して採点（100点満点） <ChevronRight size={17} />
      </button>
    </>
  );
}

function ScoreResultMock({
  maxScore,
  score,
  course,
  onBack,
}: {
  maxScore: 50 | 100;
  score: number;
  course: Course;
  onBack: () => void;
}) {
  const includesReport = maxScore === 100;
  const percentage = Math.round((score / maxScore) * 100);
  return (
    <>
      <OutlineButton onClick={onBack}><ArrowLeft size={15} /> シナリオ選択へ</OutlineButton>
      <Card style={{ marginTop: 14, textAlign: 'center', padding: '28px 20px' }}>
        <Eyebrow>{includesReport ? 'PATIENT + REPORT SCORE' : 'PATIENT INTERVIEW SCORE'}</Eyebrow>
        <h1 style={{ margin: '8px 0 0', fontSize: 22, fontWeight: 900 }}>採点結果</h1>
        <div style={{ marginTop: 22, color: COLORS.cyan }}>
          <span style={{ fontSize: 68, fontWeight: 900, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{score}</span>
          <span style={{ marginLeft: 5, fontSize: 20, fontWeight: 900 }}>/ {maxScore}点</span>
        </div>
        <div style={{ maxWidth: 360, margin: '18px auto 0' }}><ProgressBar value={percentage} /></div>
        <div style={{ marginTop: 12, color: COLORS.muted, fontSize: 12.5 }}>{course.title}</div>
      </Card>

      {includesReport ? (
        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
          <Card><Eyebrow>PATIENT INTERVIEW</Eyebrow><div style={{ marginTop: 9, fontSize: 34, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>45<span style={{ fontSize: 14, color: COLORS.muted }}> / 50</span></div></Card>
          <Card><Eyebrow>REPORT INTERVIEW</Eyebrow><div style={{ marginTop: 9, fontSize: 34, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>39<span style={{ fontSize: 14, color: COLORS.muted }}> / 50</span></div></Card>
        </div>
      ) : (
        <Card style={{ marginTop: 14 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 900 }}>今回の採点範囲</h2>
          <p style={{ margin: '8px 0 0', color: COLORS.muted, fontSize: 12.5, lineHeight: 1.7 }}>患者面接のみを50点満点で採点しています。歯科衛生士への報告は採点対象に含まれません。</p>
        </Card>
      )}

      <Card style={{ marginTop: 14 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 900 }}>フィードバック</h2>
        <div style={{ marginTop: 12, padding: 14, borderRadius: 10, background: COLORS.greenSoft, color: COLORS.green, fontSize: 12.5, fontWeight: 700, lineHeight: 1.7 }}>患者さんの発言を遮らず、必要な情報を順序よく確認できました。</div>
        <div style={{ marginTop: 10, padding: 14, borderRadius: 10, background: COLORS.amberSoft, color: COLORS.amber, fontSize: 12.5, fontWeight: 700, lineHeight: 1.7 }}>{includesReport ? '報告では、主訴と生活背景の関係を先にまとめると、さらに伝わりやすくなります。' : '次回は報告面接まで進み、情報を簡潔にまとめる練習をしてみましょう。'}</div>
      </Card>
    </>
  );
}

function RecordsView() {
  const history = [
    { label: '問診', done: 5, total: 5, recent: '6/25', points: [54, 68, 82, 100] },
    { label: 'ブラッシング指導', done: 4, total: 6, recent: '6/23', points: [67, 67, 67, 67] },
    { label: 'フロス', done: 3, total: 6, recent: '6/20', points: [22, 31, 40, 50] },
    { label: '歯間ブラシ', done: 4, total: 6, recent: '6/20', points: [82, 67, 67, 67] },
    { label: 'マウスウォッシュ', done: 2, total: 6, recent: '6/16', points: [54, 33, 33, 33] },
    { label: '電動歯ブラシ', done: 3, total: 6, recent: '6/16', points: [18, 29, 41, 50] },
    { label: 'タフトブラシ', done: 1, total: 6, recent: '6/23', points: [4, 8, 12, 17] },
    { label: '歯磨剤', done: 0, total: 6, recent: '6/25', points: [0, 0, 0, 0] },
  ];

  return (
    <>
      <Card style={{ padding: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>学習スコアの推移</h1>
        <div style={{ marginTop: 14 }}><ScoreTrendChart /></div>
        <div style={{ marginTop: 4, color: COLORS.muted, fontSize: 11.5, fontWeight: 700 }}>過去1か月</div>
      </Card>

      <Card style={{ marginTop: 14, padding: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>カテゴリ別 学習履歴</h2>
        <div style={{ marginTop: 12 }}>
          {history.map((item, index) => {
            const percent = Math.round((item.done / item.total) * 100);
            return (
              <div
                key={item.label}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '42px minmax(88px, 1fr) minmax(72px, 120px) minmax(72px, auto)',
                  alignItems: 'center',
                  gap: 12,
                  padding: '13px 0',
                  borderBottom: index === history.length - 1 ? 0 : `1px solid ${COLORS.line}`,
                }}
              >
                <span style={{ width: 38, height: 38, borderRadius: 8, display: 'grid', placeItems: 'center', color: COLORS.cyan, background: COLORS.cyanSoft }}>
                  {index % 2 === 0 ? <BookOpen size={19} /> : <Sparkles size={19} />}
                </span>
                <span style={{ fontSize: 14, fontWeight: 900, lineHeight: 1.35 }}>{item.label}</span>
                <CategorySparkline points={item.points} />
                <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 900 }}>{percent}%（{item.done}/{item.total}）</div>
                  <div style={{ marginTop: 4, color: COLORS.muted, fontSize: 11 }}>直近: {item.recent}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </>
  );
}

function ScoreTrendChart() {
  const values = [65, 70, 78, 74, 85];
  const dates = ['6/1', '6/7', '6/14', '6/21', '6/28'];
  const width = 560;
  const height = 230;
  const padding = { left: 42, right: 18, top: 24, bottom: 34 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const points = values.map((value, index) => ({
    x: padding.left + (chartWidth / (values.length - 1)) * index,
    y: padding.top + chartHeight - (value / 100) * chartHeight,
    value,
  }));
  const polyline = points.map((point) => `${point.x},${point.y}`).join(' ');
  const area = `${padding.left},${padding.top + chartHeight} ${polyline} ${padding.left + chartWidth},${padding.top + chartHeight}`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="6月1日から6月28日までの学習スコア推移">
      {[0, 20, 40, 60, 80, 100].map((value) => {
        const y = padding.top + chartHeight - (value / 100) * chartHeight;
        return (
          <g key={value}>
            <line x1={padding.left} x2={padding.left + chartWidth} y1={y} y2={y} stroke={COLORS.line} strokeWidth="1" />
            <text x={padding.left - 9} y={y + 4} textAnchor="end" fontSize="10" fontWeight="700" fill={COLORS.muted}>{value}</text>
          </g>
        );
      })}
      <polygon points={area} fill={COLORS.cyanSoft} />
      <polyline points={polyline} fill="none" stroke={COLORS.cyan} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((point, index) => (
        <g key={dates[index]}>
          <circle cx={point.x} cy={point.y} r="6" fill={COLORS.surface} stroke={COLORS.cyan} strokeWidth="4" />
          <text x={point.x} y={point.y - 13} textAnchor="middle" fontSize="11" fontWeight="900" fill={COLORS.ink}>{point.value}%</text>
          <text x={point.x} y={height - 9} textAnchor="middle" fontSize="10" fontWeight="700" fill={COLORS.muted}>{dates[index]}</text>
        </g>
      ))}
    </svg>
  );
}

function CategorySparkline({ points }: { points: number[] }) {
  const width = 140;
  const height = 44;
  const max = Math.max(100, ...points);
  const line = points.map((value, index) => {
    const x = 4 + (132 / Math.max(1, points.length - 1)) * index;
    const y = 38 - (value / max) * 31;
    return `${x},${y}`;
  }).join(' ');
  const area = `4,40 ${line} 136,40`;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="44" aria-hidden>
      <polygon points={area} fill={COLORS.cyanSoft} />
      <polyline points={line} fill="none" stroke={COLORS.cyan} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SettingsView({ goalMinutes, setGoalMinutes }: { goalMinutes: number; setGoalMinutes: (minutes: number) => void }) {
  return (
    <>
      <PageHeading eyebrow="LEARNING SETTINGS" title="学習設定" description="無理なく続けられる目標を設定します。" />
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><span style={{ width: 42, height: 42, borderRadius: 10, display: 'grid', placeItems: 'center', background: COLORS.cyanSoft, color: COLORS.cyan }}><Target size={20} /></span><div><Eyebrow>DAILY GOAL</Eyebrow><h2 style={{ margin: '5px 0 0', fontSize: 16, fontWeight: 900 }}>1日の目標時間</h2></div></div>
        <div style={{ marginTop: 20, display: 'flex', gap: 8, flexWrap: 'wrap' }}>{[10, 20, 30, 45, 60].map((minutes) => <button key={minutes} type="button" onClick={() => setGoalMinutes(minutes)} style={{ minWidth: 68, border: `1px solid ${goalMinutes === minutes ? COLORS.ink : COLORS.line}`, borderRadius: 8, padding: '10px 12px', background: goalMinutes === minutes ? COLORS.ink : COLORS.surface, color: goalMinutes === minutes ? COLORS.surface : COLORS.ink, fontSize: 13, fontWeight: 800, cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}>{minutes}分</button>)}</div>
        <div style={{ marginTop: 18, padding: 14, borderRadius: 10, background: COLORS.greenSoft, color: COLORS.green, fontSize: 12.5, fontWeight: 700, lineHeight: 1.7 }}>毎日{goalMinutes}分を続けると、週に約{Math.round(goalMinutes * 7 / 60 * 10) / 10}時間の練習になります。</div>
      </Card>
      <Card style={{ marginTop: 14 }}><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><CalendarDays size={20} color={COLORS.purple} /><div><strong style={{ display: 'block', fontSize: 14 }}>学習リマインダー</strong><span style={{ display: 'block', marginTop: 4, color: COLORS.muted, fontSize: 12 }}>平日 18:00 にお知らせ</span></div></div></Card>
    </>
  );
}

function ProfileView() {
  return (
    <>
      <PageHeading eyebrow="MY PAGE" title="マイページ" description="登録情報と最近の学習状況を確認できます。" />
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ width: 54, height: 54, borderRadius: 999, display: 'grid', placeItems: 'center', background: COLORS.cyanSoft, color: COLORS.cyan }}><UserRound size={26} /></span>
          <div><strong style={{ display: 'block', fontSize: 17 }}>山田 花子</strong><span style={{ display: 'block', marginTop: 4, color: COLORS.muted, fontSize: 12 }}>歯科衛生学科 2年</span></div>
        </div>
      </Card>
      <Card style={{ marginTop: 14 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 900 }}>最近の学習</h2>
        <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}><span>修了カリキュラム</span><strong style={{ fontVariantNumeric: 'tabular-nums' }}>18 / 26</strong></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}><span>最新スコア</span><strong style={{ fontVariantNumeric: 'tabular-nums' }}>84点</strong></div>
        </div>
      </Card>
    </>
  );
}
